import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

import HomeWorkIcon from "assets/icons/home_work.svg?react";
import settingService from "api/settingsService";
import Input from "components/Input";
import HollowButton from "components/HollowButton";
import Button from "components/Button";
import Dropdown from "components/Dropdown";
import classNames from "classnames";
import Spinner from "components/Spinner";
import { useAuth } from "react-oidc-context";
import { EdeliveryAddressContext } from "dto/EDelivery/EdeliveryAddressContext";
import { EDeliveryPersonalRole } from "dto/EDelivery/EDeliveryPersonalRole";
import { EDeliveryAddressStep } from "dto/EDelivery/EDeliveryAddressStep";
import activationService, {
  ICreateEDeliveryAddresRequest,
  ITransferEDeliveryAddresRequest,
} from "api/activationService";
import { getUserClaim } from "utils/getUserClaim";
import { NipStatus } from "dto/EDelivery/NipStatus";
import { Claims } from "dto/User/claims";
import SecondCheckbox from "components/SecondCheckbox";
import { getTrustedRoles } from "utils/edelivery/getTrustedRoles";
import { getLegalForms } from "utils/edelivery/getLegalForms";
import SearchCode from "components/SearchCode";
import SearchAddress from "components/SearchAddress";
import SearchPlace from "components/SearchPlace";
import AuthLayout from "components/AuthLayout";
import { FormControlLabel, Switch } from "@mui/material";
import { EDeliveryRequestType } from "dto/EDelivery/EDeliveryRequestType";
import { isValidNip } from "utils/IsValidNip";
import PDFViewer from "components/PDFViewer";
import SignatureIcon from "assets/icons/signature.svg?react";
import NotaIcon from "assets/icons/nota_icon.svg?react";
import { useDropzone } from "react-dropzone";
import { BodyPluginUpdate } from "components/BodyPluginUpdate";
import CPluginContext from "contexts/PluginContext";
import pluginService from "api/pluginService";
import CertContext from "contexts/CertContext";
import moment from "moment";
import { blobToBase64 } from "utils/blobToBase64";
import { readFileFromBase64 } from "utils/readFileFromBase64";
import { readBlobAsBase64 } from "utils/readAsBase64";
import DropdownMui from "components/DropdownMui";
import { useTranslation } from "react-i18next";

export enum SignStep {
  Init = 0,
  Choose = 1,
  Plugin = 2,
  Pz = 3,
  ChooseCertificate = 4,
}

const CreateRequest: FC<Props> = ({
  requestType,
  context,
  role,
  onCanceled,
  onSaved,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { plugin } = useContext(CPluginContext);

  const CompanySteps = [
    {
      type: Number(EDeliveryAddressStep.IdentityStep),
      title: t("edelivery.stepData"),
      text: t("edelivery.stepDataCompanyDescription"),
    },
    {
      type: Number(EDeliveryAddressStep.AddressStep),
      title: t("edelivery.stepAddresses"),
      text: t("edelivery.stepAddressesDescription"),
    },
    {
      type: Number(EDeliveryAddressStep.SignStep),
      title: t("edelivery.stepSignature"),
      text: t("edelivery.stepSignatureDescription"),
    },
  ];

  const KRSSteps = [
    {
      type: Number(EDeliveryAddressStep.IdentityStep),
      title: t("edelivery.stepData"),
      text: t("edelivery.stepDataPersonalDescription"),
    },
    {
      type: Number(EDeliveryAddressStep.CompanyStep),
      title: t("edelivery.stepEntityData"),
      text: t("edelivery.stepEntityDataDescription"),
    },
    {
      type: Number(EDeliveryAddressStep.AddressStep),
      title: t("edelivery.stepAddresses"),
      text: t("edelivery.stepAddressesDescription"),
    },
    {
      type: Number(EDeliveryAddressStep.SignStep),
      title: t("edelivery.stepSignature"),
      text: t("edelivery.stepSignatureDescription"),
    },
  ];

  const PersonalSteps = [
    {
      type: Number(EDeliveryAddressStep.IdentityStep),
      title: t("edelivery.stepData"),
      text: t("edelivery.stepDataPersonalDescription"),
    },
    {
      type: Number(EDeliveryAddressStep.AddressStep),
      title: t("edelivery.stepAddress"),
      text: t("edelivery.correspondenceAddress"),
    },
    {
      type: Number(EDeliveryAddressStep.SignStep),
      title: t("edelivery.stepSignature"),
      text: t("edelivery.stepSignatureDescription"),
    },
  ];

  const [step, setStep] = useState<EDeliveryAddressStep | null>(
    EDeliveryAddressStep.IdentityStep,
  );

  const { mutate: doCreateEdeliveryAddress, isLoading: isCreatingAddress } =
    activationService.useCreateEDeliveryAddress();

  const { mutate: doTransferAddress, isLoading: isTransferingAddress } =
    activationService.useTransferEDeliveryAddress();

  const { mutate: generateRequestPdf, isLoading: isLoadingPdf } =
    activationService.useGenerateRequestPdf();

  const {
    data: pluginInformation,
    isFetching: isFetchingPluginVersion,
    isError: isPluginVersionError,
    refetch: refetchPluginInformation,
    isRefetching: isRefetchingPluginVersion,
  } = pluginService.useGetVersion();

  const { data: companyData } = settingService.useGetCompanyData();

  const { data: certsData, refetch: refetchCerts } =
    pluginService.useGetCerificates();
  useContext(CertContext);
  const { mutate: signDocument } =
    pluginService.useSignDocument();

  const certs = useMemo(() => certsData?.data ?? [], [certsData]);

  const isBusy = isCreatingAddress || isTransferingAddress;

  const [showCorrespondenceAddress, setShowCorrespondenceAddress] =
    useState(false);
  const [skipButtonState, setSkipButtonState] = useState({
    show: false,
    label: t("common.skip"),
    disabled: false,
  });
  const [prevButtonState, setPrevButtonState] = useState({
    show: false,
    label: t("common.back"),
    disabled: false,
  });
  const [nextButtonState, setNextButtonState] = useState({
    show: false,
    label: "",
    disabled: false,
  });

  const buisnessAddress = context === EdeliveryAddressContext.Krs;

  const [disclosure, setDisclosure] = useState(false);
  const [acceptRules, setAcceptRules] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [pdf, setPdf] = useState<Blob>(null);
  const [signedPdf, setSignedPdf] = useState<Blob | null>(null);
  const [createdRequestSignature, setCreatedRequestSignature] =
    useState<string>("");
  const [signatureStep, setSignatureStep] = useState<SignStep>(SignStep.Init);

  const [addressRequest, setAddressRequest] = useState<{
    firstName: string;
    lastName: Nullable<string>;
    email: Nullable<string>;
    pesel: Nullable<string>;
    regon: Nullable<string>;
    krs: Nullable<string>;
    nip: Nullable<string>;
    nipAssigned: Nullable<NipStatus>;
    companyName: Nullable<string>;
    courtEnforcemantOfficerTitle: Nullable<string>;
    trustedRole: Nullable<number>;
    legalForm: Nullable<number>;
    ade: Nullable<string>;
  }>({
    firstName: getUserClaim(auth.user!, Claims.Eidas_FirstName),
    lastName: getUserClaim(auth.user!, Claims.Eidas_FamilyName),
    pesel: getUserClaim(auth.user!, Claims.Eidas_PersonIdentifier),
    email: getUserClaim(auth.user!, Claims.Email),
    regon: getUserClaim(auth.user!, Claims.EDelivery_REGON),
    krs: getUserClaim(auth.user!, Claims.EDelivery_KRS),
    nip: getUserClaim(auth.user!, Claims.Eidas_VATRegistration),
    nipAssigned: NipStatus.Assigned,
    companyName: null,
    courtEnforcemantOfficerTitle: null,
    trustedRole: null,
    legalForm: null,
    ade: null,
  });

  const [correspondenceAddress, setCorrespondenceAddress] = useState({
    zipCode: "",
    city: "",
    cityId: "",
    street: "",
    streetId: "",
    houseNumber: "",
    localNumber: "",
  });

  const [headquartersAddress, setHeadquartersAddress] = useState({
    zipCode: "",
    city: "",
    cityId: "",
    street: "",
    streetId: "",
    houseNumber: "",
    localNumber: "",
  });

  const loadPdf = () => {
    const req: ICreateEDeliveryAddresRequest = {
      Type: requestType,
      Context:
        context === EdeliveryAddressContext.Personal
          ? addressRequest.trustedRole ?? 8
          : 9,
      ProfessionalTitle: addressRequest.courtEnforcemantOfficerTitle,
      CompanyName: addressRequest.companyName,
      REGON: addressRequest.regon,
      KRS: addressRequest.krs,
      NIP: addressRequest.nip,
      NipStatus: addressRequest.nipAssigned,
      LegalForm:
        context === EdeliveryAddressContext.Jdg ? 13 : addressRequest.legalForm,
      Ade: addressRequest.ade,
      HeadquartersAddress: buisnessAddress
        ? {
            BuildingNumber: headquartersAddress.houseNumber,
            City: headquartersAddress.city,
            FlatNumber: headquartersAddress.localNumber,
            PostalCode: headquartersAddress.zipCode,
            Street: headquartersAddress.street,
          }
        : undefined,
      CorrespondenceAddress:
        !buisnessAddress || showCorrespondenceAddress
          ? {
              BuildingNumber: correspondenceAddress.houseNumber,
              City: correspondenceAddress.city,
              FlatNumber: correspondenceAddress.localNumber,
              PostalCode: correspondenceAddress.zipCode,
              Street: correspondenceAddress.street,
            }
          : undefined,
    };
    generateRequestPdf(req, {
      onSuccess(data) {
        setPdf(data.data);
      },
    });
  };

  const saveFile = async (blob: Blob) => {
    const a = document.createElement("a");
    a.download = "wniosek_ade_nota.pdf";
    a.href = URL.createObjectURL(blob);
    a.addEventListener("click", () => {
      setTimeout(() => URL.revokeObjectURL(a.href), 30 * 1000);
    });
    a.click();
  };

  const handleSignWithExternalApp = () => {
    saveFile(pdf);
    setSignatureStep(SignStep.Pz);
  };

  const signWithPlugin = () => {
    refetchCerts();
    setSignatureStep(SignStep.ChooseCertificate);
  };

  const handleSignWithPlugin = () => {
    setSignatureStep(SignStep.Plugin);
    if (!isPluginVersionError && pluginInformation?.Version) {
      signWithPlugin();
    } else {
      setInterval(() => {
        refetchPluginInformation();
      }, 1000);
    }
  };

  const handleSign = async (cert: any) => {
    setSignatureStep(SignStep.Plugin);
    const b: string = (await blobToBase64(pdf)) as string;

    signDocument(
      {
        CertificateSerialNumber: cert.SerialNumber,
        Content: b.split(",")[1],
        ContentType: "application/pdf",
        FileName: "wniosek_ade_nota.pdf",
        GenerateReport: false,
      },
      {
        onError: () => {
          //setIsLoading(false);
        },
        onSuccess: async (data: any) => {
          const fileContent = data.data.Content;
          const xades = data.data.IsXades;

          const buffer = await readFileFromBase64(
            fileContent,
            xades ? "text/xml" : "application/pdf",
          );
          const blob = new Blob([buffer]);

          setSignedPdf(blob);
        },
      },
    );
  };

  useEffect(() => {
    if (
      signatureStep === SignStep.Plugin &&
      !isPluginVersionError &&
      pluginInformation?.Version
    ) {
      signWithPlugin();
    }
  }, [pluginInformation]);

  const send = async () => {
    setStep(EDeliveryAddressStep.SendingStep);
    if (requestType === EDeliveryRequestType.Create) {
      const req: ICreateEDeliveryAddresRequest = {
        Type: requestType,
        Context:
          context === EdeliveryAddressContext.Personal
            ? addressRequest.trustedRole ?? 8
            : 9,
        ProfessionalTitle: addressRequest.courtEnforcemantOfficerTitle,
        CompanyName: addressRequest.companyName,
        REGON: addressRequest.regon,
        KRS: addressRequest.krs,
        NIP: addressRequest.nip,
        NipStatus: addressRequest.nipAssigned,
        LegalForm: addressRequest.legalForm,
        Ade: addressRequest.ade,
        HeadquartersAddress: buisnessAddress
          ? {
              BuildingNumber: headquartersAddress.houseNumber,
              City: headquartersAddress.city,
              FlatNumber: headquartersAddress.localNumber,
              PostalCode: headquartersAddress.zipCode,
              Street: headquartersAddress.street,
            }
          : undefined,
        CorrespondenceAddress:
          !buisnessAddress || showCorrespondenceAddress
            ? {
                BuildingNumber: correspondenceAddress.houseNumber,
                City: correspondenceAddress.city,
                FlatNumber: correspondenceAddress.localNumber,
                PostalCode: correspondenceAddress.zipCode,
                Street: correspondenceAddress.street,
              }
            : undefined,
        Pdf: signedPdf ? await readBlobAsBase64(signedPdf) : null,
      };

      doCreateEdeliveryAddress(req, {
        onSuccess(data) {
          if (data) {
            setCreatedRequestSignature(data.data.Signature);
            //setStep(EDeliveryAddressStep.SuccessStep);
            onSaved();
          }
        },
        onError(e) {
          setNextButtonState({
            ...nextButtonState,
            disabled: false,
          });
          setErrorMessage(e.response.data.errorCodes[0].message);
          setStep(EDeliveryAddressStep.ErrorStep);
        },
      });
    } else if (requestType === EDeliveryRequestType.Transfer) {
      const req: ITransferEDeliveryAddresRequest = {
        Type: requestType,
        EDeliveryAddress: addressRequest.ade,
        Context:
          context === EdeliveryAddressContext.Personal
            ? addressRequest.trustedRole ?? 8
            : 9,
        ProfessionalTitle: addressRequest.courtEnforcemantOfficerTitle,
        CompanyName: addressRequest.companyName,
        REGON: addressRequest.regon,
        KRS: addressRequest.krs,
        NIP: addressRequest.nip,
        NipStatus: addressRequest.nipAssigned,
        LegalForm: addressRequest.legalForm,
        Ade: addressRequest.ade,
        HeadquartersAddress: buisnessAddress
          ? {
              BuildingNumber: headquartersAddress.houseNumber,
              City: headquartersAddress.city,
              FlatNumber: headquartersAddress.localNumber,
              PostalCode: headquartersAddress.zipCode,
              Street: headquartersAddress.street,
            }
          : undefined,
        CorrespondenceAddress:
          !buisnessAddress || showCorrespondenceAddress
            ? {
                BuildingNumber: correspondenceAddress.houseNumber,
                City: correspondenceAddress.city,
                FlatNumber: correspondenceAddress.localNumber,
                PostalCode: correspondenceAddress.zipCode,
                Street: correspondenceAddress.street,
              }
            : undefined,
        Pdf: signedPdf ? await readBlobAsBase64(signedPdf) : null,
      };

      doTransferAddress(req, {
        onSuccess(data) {
          if (data) {
            setCreatedRequestSignature(data.data.Signature);
            //setStep(EDeliveryAddressStep.SuccessStep);
            onSaved();
          }
        },
        onError(e) {
          setNextButtonState({
            ...nextButtonState,
            disabled: false,
          });
          setErrorMessage(e.response.data.errorCodes[0].message);
          setStep(EDeliveryAddressStep.ErrorStep);
        },
      });
    }
  };

  const prevStepInternal = () => {
    switch (step) {
      case EDeliveryAddressStep.CompanyStep:
        setStep(EDeliveryAddressStep.IdentityStep);
        break;
      case EDeliveryAddressStep.AddressStep:
        if (context === EdeliveryAddressContext.Krs) {
          setStep(EDeliveryAddressStep.CompanyStep);
        } else {
          setStep(EDeliveryAddressStep.IdentityStep);
        }
        break;
      case EDeliveryAddressStep.SignStep:
        switch (signatureStep) {
          case SignStep.Init:
            setStep(EDeliveryAddressStep.AddressStep);
            break;
          case SignStep.Choose:
            setSignatureStep(SignStep.Init);
            break;
          case SignStep.ChooseCertificate:
            setSignatureStep(SignStep.Choose);
            break;
          case SignStep.Plugin:
          case SignStep.Pz:
            setSignatureStep(SignStep.Choose);
            break;
        }

        break;
      case EDeliveryAddressStep.SummaryStep:
        setStep(EDeliveryAddressStep.AddressStep);
        break;
    }
  };

  const nextStepInternal = () => {
    switch (step) {
      case EDeliveryAddressStep.ContextStep:
        break;
      case EDeliveryAddressStep.IdentityStep:
        if (context === EdeliveryAddressContext.Krs) {
          setStep(EDeliveryAddressStep.CompanyStep);
        } else {
          setStep(EDeliveryAddressStep.AddressStep);
        }
        break;
      case EDeliveryAddressStep.CompanyStep: {
        setStep(EDeliveryAddressStep.AddressStep);
        break;
      }
      case EDeliveryAddressStep.AddressStep:
        setSignatureStep(SignStep.Init);
        setStep(EDeliveryAddressStep.SignStep);
        break;
      case EDeliveryAddressStep.SignStep:
        switch (signatureStep) {
          case SignStep.Init:
            setSignatureStep(SignStep.Choose);
            break;
        }
        // send();
        break;
      case EDeliveryAddressStep.SuccessStep:
      case EDeliveryAddressStep.ErrorStep:
        onSaved();
        break;
    }
  };

  const getTitle = () => {
    switch (requestType) {
      case EDeliveryRequestType.Create:
        switch (context) {
          case EdeliveryAddressContext.Krs:
            return t("edelivery.createForLegalPerson");
          case EdeliveryAddressContext.Jdg:
            return t("edelivery.createForJdg");
          case EdeliveryAddressContext.Personal:
            switch (role) {
              case EDeliveryPersonalRole.Citizen:
                return t("edelivery.createPersonal");
              case EDeliveryPersonalRole.TrustedRole:
                return t("edelivery.createForTrustedRole");
            }
        }
      case EDeliveryRequestType.Transfer:
        switch (context) {
          case EdeliveryAddressContext.Krs:
            return t("edelivery.transferForLegalPerson");
          case EdeliveryAddressContext.Jdg:
            return t("edelivery.transferForJdg");
          case EdeliveryAddressContext.Personal:
            switch (role) {
              case EDeliveryPersonalRole.Citizen:
                return t("edelivery.transferPersonal");
              case EDeliveryPersonalRole.TrustedRole:
                return t("edelivery.transferForTrustedRole");
            }
        }
      case EDeliveryRequestType.Update:
        return t("edelivery.updateRequest");
      case EDeliveryRequestType.Close:
        return t("edelivery.closeRequest");
      case EDeliveryRequestType.Undisclosure:
        return t("edelivery.undisclosureRequest");
      case EDeliveryRequestType.ExtensePeriod:
        return t("edelivery.extendRequest");
      case EDeliveryRequestType.Disclosure:
        switch (context) {
          case EdeliveryAddressContext.Jdg:
            return t("edelivery.disclosureForJdg");
          case EdeliveryAddressContext.Personal:
            switch (role) {
              case EDeliveryPersonalRole.Citizen:
                return t("edelivery.disclosurePersonal");
              case EDeliveryPersonalRole.TrustedRole:
                return t("edelivery.disclosureForTrustedRole");
            }
        }
    }
  };

  useEffect(() => {
    if (auth.user) {
      setAddressRequest((prevState) => ({
        ...prevState,
        firstName: getUserClaim(auth.user!, Claims.Eidas_FirstName),
        lastName: getUserClaim(auth.user!, Claims.Eidas_FamilyName),
        pesel: getUserClaim(auth.user!, Claims.Eidas_PersonIdentifier),
      }));
    }
  }, [auth.user]);

  useEffect(() => {
    setAddressRequest({ ...addressRequest, trustedRole: null });
    // setPersonalRole(null);
  }, [context]);

  useEffect(() => {
    switch (step) {
      case EDeliveryAddressStep.IdentityStep:
        setSkipButtonState({ ...skipButtonState, show: false });
        setPrevButtonState({
          ...prevButtonState,
          show: false,
          label: t("common.back"),
        });
        setNextButtonState({ ...nextButtonState, show: true, label: t("common.next") });
        break;
      case EDeliveryAddressStep.CompanyStep:
      case EDeliveryAddressStep.AddressStep:
        setSkipButtonState({ ...skipButtonState, show: false });
        setPrevButtonState({ ...prevButtonState, show: true, label: t("common.back") });
        setNextButtonState({
          ...nextButtonState,
          show: true,
          label: t("common.next"),
        });
        break;
      case EDeliveryAddressStep.SignStep:
        setSkipButtonState({ ...skipButtonState, show: false });
        setPrevButtonState({ ...prevButtonState, show: true, label: t("common.back") });
        if (signatureStep === SignStep.Pz) {
          setNextButtonState({
            ...nextButtonState,
            show: true,
            label: t("edelivery.submitRequest"),
          });
        } else {
          setNextButtonState({
            ...nextButtonState,
            show: true,
            label: t("edelivery.sign"),
          });
        }

        if (signatureStep === SignStep.Init) {
          loadPdf();
        }
        break;
      case EDeliveryAddressStep.SuccessStep:
      case EDeliveryAddressStep.ErrorStep:
        setSkipButtonState({ ...skipButtonState, show: false });
        setPrevButtonState({
          ...prevButtonState,
          show: false,
          label: t("common.back"),
        });
        setNextButtonState({
          ...nextButtonState,
          show: true,
          label: t("common.close"),
          disabled: false,
        });
        break;
    }
  }, [step, context, signatureStep]);

  useEffect(() => {
    if (signedPdf) {
      send();
    }
  }, [signedPdf]);

  const onDrop = useCallback((acceptedFiles: any) => {
    setSignedPdf(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      accept: { "application/pdf": [".pdf"] },
      onDrop: onDrop,
      multiple: false,
    });

  useEffect(() => {
    if (companyData?.data) {
      setAddressRequest({
        ...addressRequest,
        companyName: companyData.data.Name,
        krs: companyData.data.KRS,
        legalForm: companyData.data.LegalForm,
        nip: companyData.data.NIP,
        nipAssigned:
          companyData.data.NIP && companyData.data.NIP.length > 0
            ? NipStatus.Assigned
            : NipStatus.Blank,
        regon: companyData.data.REGON,
      });

      if (companyData.data.HeadquartersAddress) {
        setHeadquartersAddress({
          ...headquartersAddress,
          street: companyData.data.HeadquartersAddress.street ?? "",
          houseNumber:
            companyData.data.HeadquartersAddress.buildingNumber ?? "",
          localNumber: companyData.data.HeadquartersAddress.flatNumber ?? "",
          zipCode: companyData.data.HeadquartersAddress.postalCode ?? "",
          city: companyData.data.HeadquartersAddress.city ?? "",
        });
      }
    }
  }, [companyData?.data]);

  const canGoNext = () => {
    if (step === EDeliveryAddressStep.IdentityStep) {
      if (!acceptRules) return false;

      if (role === EDeliveryPersonalRole.TrustedRole) {
        if (!addressRequest.trustedRole) return false;
      }

      if (context === EdeliveryAddressContext.Jdg) {
        if (
          addressRequest.nipAssigned === NipStatus.Assigned &&
          (!addressRequest.nip ||
            addressRequest.nip.length === 0 ||
            !isValidNip(addressRequest.nip))
        ) {
          return false;
        }

        if (!addressRequest.regon || addressRequest.regon.length < 9) {
          return false;
        }

        if (
          !addressRequest.companyName ||
          addressRequest.companyName.length === 0
        ) {
          return false;
        }
      }
    }

    if (
      step === EDeliveryAddressStep.CompanyStep &&
      context === EdeliveryAddressContext.Krs
    ) {
      if (
        addressRequest.nipAssigned === NipStatus.Assigned &&
        (addressRequest.nip.length === 0 || !isValidNip(addressRequest.nip))
      ) {
        return false;
      }

      if (!addressRequest.legalForm) {
        return false;
      }

      if (
        !addressRequest.companyName ||
        addressRequest.companyName.length === 0
      ) {
        return false;
      }
    }

    if (step === EDeliveryAddressStep.AddressStep) {
      if (
        !acceptRules &&
        (requestType === EDeliveryRequestType.Create ||
          requestType === EDeliveryRequestType.Transfer)
      )
        return false;
      // if (
      //   !disclosure &&
      //   (requestType === EDeliveryRequestType.Create ||
      //     requestType === EDeliveryRequestType.Disclosure)
      // )
      //   return false;

      if (buisnessAddress) {
        if (
          !headquartersAddress.zipCode ||
          headquartersAddress.zipCode.length === 0
        ) {
          return false;
        }
        if (
          !headquartersAddress.city ||
          headquartersAddress.city.length === 0
        ) {
          return false;
        }

        if (
          !headquartersAddress.street ||
          headquartersAddress.street.length === 0
        ) {
          return false;
        }

        if (
          !headquartersAddress.houseNumber ||
          headquartersAddress.houseNumber.length === 0
        ) {
          return false;
        }
      }

      if (!buisnessAddress || (buisnessAddress && showCorrespondenceAddress)) {
        if (
          !correspondenceAddress.zipCode ||
          correspondenceAddress.zipCode.length === 0
        ) {
          return false;
        }
        if (
          !correspondenceAddress.city ||
          correspondenceAddress.city.length === 0
        ) {
          return false;
        }

        if (
          !correspondenceAddress.street ||
          correspondenceAddress.street.length === 0
        ) {
          return false;
        }

        if (
          !correspondenceAddress.houseNumber ||
          correspondenceAddress.houseNumber.length === 0
        ) {
          return false;
        }
      }
    }

    if (step === EDeliveryAddressStep.SignStep) {
      switch (signatureStep) {
        case SignStep.Init:
          if (!pdf) return false;

          break;
        case SignStep.Pz:
          if (!signedPdf) return false;

          break;
      }
    }

    return true;
  };

  return (
    <AuthLayout
      actualStep={Number(step)}
      steps={
        context === EdeliveryAddressContext.Jdg
          ? CompanySteps
          : context === EdeliveryAddressContext.Krs
          ? KRSSteps
          : PersonalSteps
      }
      handleBack={onCanceled}
      backLabel={t("edelivery.backToApp")}
    >
      <div className={styles.wrapper}>
        <div className={styles.wrapperCenter}>
          <div
            className={classNames(
              styles.content,
              step === EDeliveryAddressStep.SignStep &&
                signatureStep === SignStep.Init
                ? styles.pdf
                : "",
            )}
          >
            {(step === EDeliveryAddressStep.IdentityStep ||
              step === EDeliveryAddressStep.AddressStep) && (
              <>
                <h1>{t("edelivery.request")}</h1>
                <h3>{getTitle()}</h3>
              </>
            )}
            {step === EDeliveryAddressStep.SignStep &&
              signatureStep === SignStep.Init && (
                <>
                  <h1>{t("edelivery.requestPreview")}</h1>
                  <h3>{t("edelivery.verifyAndSign")}</h3>
                </>
              )}
            {step === EDeliveryAddressStep.SignStep &&
              signatureStep === SignStep.Choose && (
                <>
                  <h1>{t("edelivery.signatureType")}</h1>
                  <h3>{t("edelivery.chooseSigningMethod")}</h3>
                </>
              )}
            {step === EDeliveryAddressStep.SignStep &&
              signatureStep === SignStep.ChooseCertificate && (
                <>
                  <h1>{t("edelivery.signatureCertificate")}</h1>
                  <h3>{t("edelivery.chooseCertificateHint")}</h3>
                </>
              )}
            {step === EDeliveryAddressStep.ErrorStep && (
              <>
                <h1>{t("edelivery.requestCreateError")}</h1>
                <h3>{t("edelivery.requestSubmitFailed")}</h3>
              </>
            )}
            {step === EDeliveryAddressStep.SuccessStep && (
              <>
                <h1>{t("edelivery.sent")}</h1>
                <h3>{t("edelivery.requestSubmittedCorrectly")}</h3>
              </>
            )}
            {step === EDeliveryAddressStep.IdentityStep && (
              <>
                <div className={styles.form}>
                  <div className={styles.group}>
                    <Input
                      type="text"
                      label={t("forms.firstName")}
                      value={addressRequest.firstName}
                      onChange={() => {}}
                      disabled={true}
                    ></Input>
                    <Input
                      type="text"
                      label={t("forms.lastName")}
                      value={addressRequest.lastName}
                      onChange={() => {}}
                      disabled={true}
                    ></Input>
                  </div>
                  <div className={styles.group}>
                    <Input
                      type="text"
                      label="PESEL"
                      value={addressRequest.pesel}
                      onChange={() => {}}
                      disabled={true}
                    ></Input>
                  </div>
                  <Input
                    type="text"
                    label="Email"
                    value={addressRequest.email}
                    onChange={() => {}}
                    disabled={true}
                  ></Input>

                  {role === EDeliveryPersonalRole.TrustedRole && (
                    <div className={styles.trustedRole}>
                      <Dropdown
                        className={styles.trustedRoleDropdown}
                        onChange={(r) =>
                          setAddressRequest({
                            ...addressRequest,
                            trustedRole: r ? (r.value as number) : null,
                          })
                        }
                        list={getTrustedRoles()}
                        label={t("edelivery.professionalTitle")}
                        placeholder={t("edelivery.professionalTitle")}
                        allowNull={true}
                        defaultValue={null}
                      />
                    </div>
                  )}

                  {addressRequest.trustedRole === 14 && (
                    <>
                      <Input
                        id="regon"
                        type="text"
                        label="REGON"
                        value={addressRequest.regon}
                        onChange={(v) => {
                          setAddressRequest({ ...addressRequest, regon: v });
                        }}
                      ></Input>
                    </>
                  )}

                  {context === EdeliveryAddressContext.Jdg && (
                    <>
                      <div className={styles.group}>
                        <Input
                          id="nip"
                          type="text"
                          label="NIP"
                          value={addressRequest.nip}
                          onChange={(v) => {
                            setAddressRequest({ ...addressRequest, nip: v });
                          }}
                        ></Input>
                        <Input
                          id="regon"
                          type="text"
                          label="REGON"
                          value={addressRequest.regon}
                          onChange={(v) => {
                            setAddressRequest({ ...addressRequest, regon: v });
                          }}
                        ></Input>
                      </div>
                      <Input
                        id="companyName"
                        type="text"
                        label={t("settings.companyName")}
                        value={addressRequest.companyName}
                        onChange={(v) => {
                          setAddressRequest({
                            ...addressRequest,
                            companyName: v,
                          });
                        }}
                      ></Input>
                    </>
                  )}

                  {addressRequest.trustedRole === 14 && (
                    <>
                      <Input
                        type="text"
                        label={t("edelivery.bailiffTitle")}
                        value={addressRequest.courtEnforcemantOfficerTitle}
                        onChange={(v) => {
                          setAddressRequest({
                            ...addressRequest,
                            courtEnforcemantOfficerTitle: v,
                          });
                        }}
                      ></Input>
                    </>
                  )}

                  {requestType === EDeliveryRequestType.Transfer && (
                    <Input
                      id="ade"
                      type="text"
                      label={t("edelivery.electronicAddress")}
                      value={addressRequest.ade ?? ""}
                      onChange={(v) => {
                        setAddressRequest({ ...addressRequest, ade: v });
                      }}
                    ></Input>
                  )}
                </div>
              </>
            )}
            {step === EDeliveryAddressStep.CompanyStep && (
              <>
                <div className={styles.form}>
                  <div className={styles.group}>
                    <Input
                      id="companyName"
                      type="text"
                      label={t("edelivery.entityName")}
                      value={addressRequest.companyName ?? ""}
                      onChange={(v) => {
                        setAddressRequest({
                          ...addressRequest,
                          companyName: v,
                        });
                      }}
                    ></Input>
                  </div>
                  <div className={styles.group}>
                    <DropdownMui
                      list={getLegalForms()}
                      label={t("edelivery.legalForm")}
                      placeholder={t("edelivery.chooseLegalForm")}
                      allowNull={true}
                      defaultValue={addressRequest.legalForm}
                      onChange={(r) =>
                        setAddressRequest({
                          ...addressRequest,
                          legalForm: r ? (r.value as number) : null,
                        })
                      }
                    ></DropdownMui>
                  </div>
                  <div className={styles.group}>
                    <Input
                      id="krs"
                      type="text"
                      label={t("edelivery.krsNumber")}
                      value={addressRequest.krs ?? ""}
                      onChange={(v) => {
                        setAddressRequest({ ...addressRequest, krs: v });
                      }}
                    ></Input>
                    <Input
                      id="regon"
                      type="text"
                      label={t("edelivery.regonNumber")}
                      value={addressRequest.regon ?? ""}
                      onChange={(v) => {
                        setAddressRequest({ ...addressRequest, regon: v });
                      }}
                    ></Input>
                  </div>

                  <Input
                    id="nip"
                    type="text"
                    label={t("edelivery.nipNumber")}
                    value={addressRequest.nip ?? ""}
                    onChange={(v) => {
                      setAddressRequest({ ...addressRequest, nip: v });
                    }}
                    disabled={
                      addressRequest.nipAssigned === NipStatus.Blank ||
                      addressRequest.nipAssigned === NipStatus.Canceled ||
                      addressRequest.nipAssigned === NipStatus.Revoked
                    }
                  ></Input>

                  <div className={styles.checkboxes}>
                    <SecondCheckbox
                      value={addressRequest.nipAssigned === NipStatus.Blank}
                      text={t("edelivery.nipNotAssigned")}
                      onChange={(r) =>
                        setAddressRequest({
                          ...addressRequest,
                          nipAssigned: r ? NipStatus.Blank : NipStatus.Assigned,
                          nip: null,
                        })
                      }
                    />
                    <SecondCheckbox
                      value={addressRequest.nipAssigned === NipStatus.Canceled}
                      text={t("edelivery.nipInvalidated")}
                      onChange={(r) =>
                        setAddressRequest({
                          ...addressRequest,
                          nipAssigned: r
                            ? NipStatus.Canceled
                            : NipStatus.Assigned,
                          nip: null,
                        })
                      }
                    />
                    <SecondCheckbox
                      value={addressRequest.nipAssigned === NipStatus.Revoked}
                      text={t("edelivery.nipRevoked")}
                      onChange={(r) =>
                        setAddressRequest({
                          ...addressRequest,
                          nipAssigned: r
                            ? NipStatus.Revoked
                            : NipStatus.Assigned,
                          nip: null,
                        })
                      }
                    />
                  </div>
                </div>
              </>
            )}
            {step === EDeliveryAddressStep.AddressStep && (
              <>
                <div className={styles.form}>
                  {buisnessAddress && (
                    <div className={styles.addressBlock}>
                      <div className={styles.subHeader}>
                        <HomeWorkIcon></HomeWorkIcon> {t("edelivery.headquartersAddress")}
                      </div>
                      <div className={styles.group}>
                        <SearchCode
                          id="zipCode"
                          // isError={
                          //   touched.ZipCode ? (errors.ZipCode as string) : ''
                          // }
                          defaultValue={headquartersAddress.zipCode}
                          onChange={(t) => {
                            setHeadquartersAddress({
                              ...headquartersAddress,
                              zipCode: t,
                            });
                          }}
                        />
                        <SearchPlace
                          id="place"
                          // isError={touched.City ? (errors.City as string) : ''}
                          zipCode={headquartersAddress.zipCode}
                          defaultValue={headquartersAddress.city}
                          onChange={(t, c) => {
                            setHeadquartersAddress({
                              ...headquartersAddress,
                              city: t,
                              cityId: c,
                            });
                          }}
                        />
                      </div>
                      <SearchAddress
                        // isError={touched.Address ? (errors.Address as string) : ''}
                        defaultValue={headquartersAddress.street}
                        cityId={headquartersAddress.cityId}
                        onChange={(t, c) => {
                          setHeadquartersAddress({
                            ...headquartersAddress,
                            street: t,
                            streetId: c,
                          });
                        }}
                        allowValueNotFromList={true}
                      />
                      <div className={styles.group}>
                        <Input
                          id="houseNumber"
                          type="text"
                          label={t("edelivery.buildingNumber")}
                          defaultValue={headquartersAddress.houseNumber}
                          onChange={(v) => {
                            setHeadquartersAddress({
                              ...headquartersAddress,
                              houseNumber: v,
                            });
                          }}
                        ></Input>
                        <Input
                          id="localNumber"
                          type="text"
                          label={t("edelivery.flatNumber")}
                          defaultValue={headquartersAddress.localNumber}
                          onChange={(v) => {
                            setHeadquartersAddress({
                              ...headquartersAddress,
                              localNumber: v,
                            });
                          }}
                        ></Input>
                      </div>
                    </div>
                  )}
                  {buisnessAddress && (
                    <FormControlLabel
                      control={
                        <Switch
                          title={t("edelivery.addCorrespondenceAddress")}
                          checked={showCorrespondenceAddress}
                          onChange={(e, s) => {
                            setShowCorrespondenceAddress(s);
                          }}
                        ></Switch>
                      }
                      label={t("edelivery.addCorrespondenceAddress")}
                    />
                  )}

                  {(showCorrespondenceAddress || !buisnessAddress) && (
                    <div className={styles.addressBlock}>
                      <div className={styles.subHeader}>
                        <HomeWorkIcon></HomeWorkIcon> {t("edelivery.correspondenceAddress")}
                      </div>
                      <div className={styles.group}>
                        <SearchCode
                          // isError={
                          //   touched.ZipCode ? (errors.ZipCode as string) : ''
                          // }
                          defaultValue={correspondenceAddress.zipCode}
                          onChange={(t) => {
                            setCorrespondenceAddress({
                              ...correspondenceAddress,
                              zipCode: t,
                            });
                          }}
                        />
                        <SearchPlace
                          // isError={touched.City ? (errors.City as string) : ''}
                          zipCode={correspondenceAddress.zipCode}
                          defaultValue={correspondenceAddress.city}
                          onChange={(t, c) => {
                            setCorrespondenceAddress({
                              ...correspondenceAddress,
                              city: t,
                              cityId: c,
                            });
                          }}
                        />
                      </div>
                      <SearchAddress
                        // isError={touched.Address ? (errors.Address as string) : ''}
                        defaultValue={correspondenceAddress.street}
                        cityId={correspondenceAddress.cityId}
                        onChange={(t, c) => {
                          setCorrespondenceAddress({
                            ...correspondenceAddress,
                            street: t,
                            streetId: c,
                          });
                        }}
                        allowValueNotFromList={true}
                      />
                      <div className={styles.group}>
                        <Input
                          type="text"
                          label={t("edelivery.buildingNumber")}
                          defaultValue={correspondenceAddress.houseNumber}
                          onChange={(v) => {
                            setCorrespondenceAddress({
                              ...correspondenceAddress,
                              houseNumber: v,
                            });
                          }}
                        ></Input>
                        <Input
                          type="text"
                          label={t("edelivery.flatNumber")}
                          defaultValue={correspondenceAddress.localNumber}
                          onChange={(v) => {
                            setCorrespondenceAddress({
                              ...correspondenceAddress,
                              localNumber: v,
                            });
                          }}
                        ></Input>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
            {(step === EDeliveryAddressStep.IdentityStep ||
              step === EDeliveryAddressStep.AddressStep) && (
              <div className={styles.checkboxes}>
                {(requestType === EDeliveryRequestType.Create ||
                  requestType === EDeliveryRequestType.Transfer) && (
                  <SecondCheckbox
                    value={acceptRules}
                    text={t("edelivery.agreeKfjOperator")}
                    onChange={(t) => {
                      setAcceptRules(t);
                    }}
                  />
                )}
                {(requestType === EDeliveryRequestType.Create ||
                  requestType === EDeliveryRequestType.Disclosure) && (
                  <SecondCheckbox
                    value={disclosure}
                    text={t("edelivery.disclosureConsent")}
                    onChange={(t) => {
                      setDisclosure(t);
                    }}
                  />
                )}

                {/* {errorMessage && (
                  <div className={styles.warn}>
                    <b>{errorMessage}</b>
                  </div>
                )}
                {(isCreatingAddress || isTransferingAddress) && (
                  <div className={styles.verificaionInProgress}>
                    <Spinner className={styles.spinner} />
                    <p>Trwa składanie wniosku</p>
                  </div>
                )} */}
              </div>
            )}
            {step === EDeliveryAddressStep.SignStep && (
              <>
                {signatureStep === SignStep.Init && (
                  <div className={styles.viewer}>
                    {isLoadingPdf && (
                      <div className={styles.loading}>
                        <Spinner></Spinner>
                        <div>{t("edelivery.generatingRequest")}</div>
                      </div>
                    )}
                    {!isLoadingPdf && (
                      <PDFViewer
                        file={pdf}
                        showHeader={false}
                        withoutBorder={true}
                        className={styles.nobg}
                        showSignaturePanel={false}
                      ></PDFViewer>
                    )}
                  </div>
                )}
                {signatureStep === SignStep.Choose && (
                  <>
                    <div className={styles.hint}>
                      {t("edelivery.chooseSigningMethodHint")}
                    </div>
                    <div className={styles.methods}>
                      <div
                        className={styles.method}
                        onClick={() => handleSignWithPlugin()}
                      >
                        <div className={styles.icon}>
                          <NotaIcon></NotaIcon>
                        </div>
                        {t("edelivery.signQualified")}
                      </div>
                      <div
                        className={styles.method}
                        onClick={() => handleSignWithExternalApp()}
                      >
                        <div className={styles.icon}>
                          <SignatureIcon></SignatureIcon>
                        </div>
                        {t("edelivery.signTrustedProfile")}
                      </div>
                    </div>
                  </>
                )}
                {signatureStep === SignStep.Plugin && (
                  <>
                    {(isPluginVersionError ||
                      isRefetchingPluginVersion ||
                      isFetchingPluginVersion ||
                      !pluginInformation?.Version) && (
                      <div className={styles.noPlugin}>
                        <BodyPluginUpdate
                          className={styles.wtyczka}
                          isWindows={plugin.isWindows}
                          isMac={plugin.isMac}
                          linkWindows={plugin.linkWindows}
                          linkMac={plugin.linkMac}
                          title={t("edelivery.pluginNotFound")}
                          hint={t("edelivery.pluginHint")}
                        />
                      </div>
                    )}
                    {!isPluginVersionError &&
                      !isRefetchingPluginVersion &&
                      !isFetchingPluginVersion &&
                      pluginInformation?.Version && (
                        <div className={styles.signing}>
                          <b>{t("edelivery.signingInProgress")}</b>
                          <div>{t("edelivery.followAppInstructions")}</div>
                          <div>
                            <Spinner></Spinner>
                          </div>
                        </div>
                      )}
                  </>
                )}
                {signatureStep === SignStep.ChooseCertificate && (
                  <div className={styles.list}>
                    {certs.map((cert: any) => (
                      <div
                        onClick={() => handleSign(cert)}
                        className={styles.box}
                      >
                        <div className={styles.title}>
                          <div className={styles.name}>{cert.Name}</div>
                          <div className={styles.date}>
                            {moment(cert.ValidTo)
                              .format("DD.MM.YYYY")
                              .toString()}
                          </div>
                        </div>
                        <div className={styles.cert}>{cert.IssuerBy}</div>
                      </div>
                    ))}
                    {!certs.length && (
                      <div>
                        {t("edelivery.noCertificates")}
                      </div>
                    )}
                  </div>
                )}
                {signatureStep === SignStep.Pz && (
                  <>
                    <div
                      className={classNames(
                        styles.dropContainer,
                        isDragAccept ? styles.dropAccept : "",
                        isDragReject ? styles.dropReject : "",
                        isFocused ? styles.dropFocus : "",
                      )}
                    >
                      <div {...getRootProps()}>
                        <input {...getInputProps()} />
                        {!signedPdf && (
                          <>
                            <p>
                              {t("edelivery.dropSignedRequest")}<br></br> {t("common.or")}
                            </p>
                            <div className={styles.buttonCTA}>
                              {t("common.chooseFromDisk")}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className={styles.warn}>
                      <b>{errorMessage}</b>
                    </div>
                  </>
                )}
              </>
            )}
            {step === EDeliveryAddressStep.SendingStep && (
              <>
                <div className={styles.sending}>
                  <div>
                    <b>{t("edelivery.submittingRequest")}</b>
                  </div>
                  <Spinner></Spinner>
                </div>
              </>
            )}
            {step === EDeliveryAddressStep.SuccessStep && (
              <>
                <div className={styles.subHeader}>
                  <b>{t("edelivery.requestId")}:</b>
                </div>
                <div className={styles.subHeader}>
                  {createdRequestSignature}
                </div>
              </>
            )}
            {step === EDeliveryAddressStep.ErrorStep && (
              <>
                <div>
                  <b>{t("common.details")}:</b>
                </div>
                <div className={styles.hint}>{errorMessage}</div>
              </>
            )}
          </div>
        </div>
        <div className={styles.buttons}>
          {skipButtonState.show && !isBusy && (
            <HollowButton
              className={styles.button}
              text={t("common.cancel")}
              onClick={onCanceled}
            ></HollowButton>
          )}
          {prevButtonState.show && !isBusy && (
            <HollowButton
              className={styles.button}
              text={prevButtonState.label}
              onClick={prevStepInternal}
            ></HollowButton>
          )}
          {nextButtonState.show && (
            <Button
              disabled={!canGoNext()}
              className={styles.button}
              text={nextButtonState.label}
              onClick={nextStepInternal}
              loading={isBusy}
            ></Button>
          )}
        </div>
        <div className={classNames(styles.hint, styles.disconnect)}></div>
      </div>
    </AuthLayout>
  );
};

export default CreateRequest;
